import { useCallback, useRef, useState } from "react";

// Deterministic snapshot-based history with a baseline and index pointer
export type RefineHistoryEntry<T> = {
  id: string;
  payload: T;
  timestamp: number;
};

type UseRefineHistoryReturn<T> = {
  // Full ordered history: index 0 is baseline (never dropped)
  entries: RefineHistoryEntry<T>[];
  // Pointer to current snapshot in entries
  index: number;
  // Convenience accessor for current entry
  current: RefineHistoryEntry<T> | undefined;

  // Capabilities
  canUndo: boolean;
  canRedo: boolean;

  // Operations
  seed: (initial: T) => void;
  push: (next: T) => string;
  undo: () => T | undefined;
  redo: () => T | undefined;
  resetToBaseline: () => T | undefined;
  clear: () => void;
};

export function useRefineHistory<T>(limit: number = 10): UseRefineHistoryReturn<T> {
  const [entries, setEntries] = useState<RefineHistoryEntry<T>[]>([]);
  const [index, setIndex] = useState<number>(-1);
  const nextIdRef = useRef<number>(1);

  const canUndo = index > 0;
  const canRedo = index >= 0 && index < entries.length - 1;

  const seed = useCallback((initial: T) => {
    const id = `refine-${nextIdRef.current}`;
    nextIdRef.current += 1;
    setEntries([{ id, payload: initial, timestamp: Date.now() }]);
    setIndex(0);
  }, []);

  const push = useCallback(
    (next: T): string => {
      const id = `refine-${nextIdRef.current}`;
      nextIdRef.current += 1;

      setEntries(prev => {
        const effectiveIndex = Math.min(Math.max(index, -1), prev.length - 1);
        const base = effectiveIndex >= 0 ? prev.slice(0, effectiveIndex + 1) : prev.slice(0);

        const nextEntries = [...base, { id, payload: next, timestamp: Date.now() }];

        const capacity = limit + 1;
        const trimmedEntries =
          nextEntries.length > capacity
            ? nextEntries.slice(nextEntries.length - capacity)
            : nextEntries;

        // Move pointer to tail deterministically
        setIndex(trimmedEntries.length - 1);
        return trimmedEntries;
      });

      return id;
    },
    [index, limit]
  );

  const undo = useCallback(() => {
    if (index <= 0) return undefined;
    const nextIndex = index - 1;
    setIndex(nextIndex);
    return entries[nextIndex]?.payload;
  }, [entries, index]);

  const redo = useCallback(() => {
    if (index < 0 || index >= entries.length - 1) return undefined;
    const nextIndex = index + 1;
    setIndex(nextIndex);
    return entries[nextIndex]?.payload;
  }, [entries, index]);

  const resetToBaseline = useCallback(() => {
    if (entries.length === 0) return undefined;
    setIndex(0);
    return entries[0]?.payload;
  }, [entries]);

  const clear = useCallback(() => {
    setEntries([]);
    setIndex(-1);
  }, []);

  const current = index >= 0 ? entries[index] : undefined;

  return {
    entries,
    index,
    current,
    canUndo,
    canRedo,
    seed,
    push,
    undo,
    redo,
    resetToBaseline,
    clear
  } as const;
}
